import React, { useState, useCallback, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Play, 
  Pause,
  SkipBack,
  SkipForward,
  RefreshCw,
  GitBranch,
  Zap,
  Circle,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Live Execution Overlay - Collapse Design/Run Boundary
 * 
 * This overlay provides quick simulation controls directly on the graph view,
 * eliminating the need to switch to a separate "Simulate" mode.
 * 
 * "The same IR and interpreter should be used for all - 
 *  a machine can be simulated, then seamlessly continue in execution, 
 *  be replayed, and forked."
 */
const LiveExecutionOverlay = ({ 
  machineId, 
  machine,
  activeState,
  onStateChange,
  onTraceUpdate,
  compact = false
}) => {
  const [session, setSession] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(false);
  const [stepCount, setStepCount] = useState(0);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  // Start/resume execution
  const startExecution = useCallback(async () => {
    if (!machineId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/machines/${machineId}/simulation/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ runtime_profile: 'simulation' })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSession({ id: data.session_id, profile: data.runtime_profile });
        setSnapshot(data.snapshot);
        setAvailableEvents(data.available_events || []);
        setStepCount(0);
        onStateChange?.(data.snapshot?.active_state);
        toast.success('Execution started');
      }
    } catch (error) {
      console.error('Failed to start execution:', error);
      toast.error('Failed to start execution');
    } finally {
      setLoading(false);
    }
  }, [machineId, apiUrl, onStateChange]);

  // Dispatch event
  const dispatchEvent = useCallback(async () => {
    if (!session?.id || !selectedEvent) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/dispatch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_name: selectedEvent, payload: {} })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSnapshot(data.snapshot);
        setAvailableEvents(data.available_events || []);
        setSelectedEvent('');
        setStepCount(prev => prev + 1);
        onStateChange?.(data.snapshot?.active_state);
        onTraceUpdate?.(prev => [...(prev || []), data.entry]);
        
        if (data.entry?.transition_taken) {
          toast.success(`→ ${data.entry.to_state}`);
        }
      }
    } catch (error) {
      console.error('Failed to dispatch:', error);
      toast.error('Failed to dispatch event');
    } finally {
      setLoading(false);
    }
  }, [session, selectedEvent, apiUrl, onStateChange, onTraceUpdate]);

  // Step back
  const stepBack = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/step-back`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSnapshot(data.snapshot);
        setAvailableEvents(data.available_events || []);
        onStateChange?.(data.snapshot?.active_state);
        toast.info('Stepped back');
      }
    } catch (error) {
      toast.info('Already at initial state');
    }
  }, [session, apiUrl, onStateChange]);

  // Reset
  const resetExecution = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const response = await fetch(
        `${apiUrl}/api/simulation/${session.id}/reset`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSnapshot(data.snapshot);
        setAvailableEvents(data.available_events || []);
        setStepCount(0);
        onStateChange?.(data.snapshot?.active_state);
        toast.info('Execution reset');
      }
    } catch (error) {
      toast.error('Failed to reset');
    }
  }, [session, apiUrl, onStateChange]);

  // Stop execution
  const stopExecution = useCallback(() => {
    setSession(null);
    setSnapshot(null);
    setAvailableEvents([]);
    setSelectedEvent('');
    setStepCount(0);
    onStateChange?.(null);
  }, [onStateChange]);

  // Compact mode - just the status indicator
  if (compact && !session) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={startExecution}
              disabled={!machineId || loading}
              className="h-7 px-2 text-xs"
            >
              <Play className="w-3.5 h-3.5 mr-1" />
              Run
            </Button>
          </TooltipTrigger>
          <TooltipContent>Start live execution on graph</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-black/40 rounded-lg border border-white/10">
        <Circle className="w-3 h-3 text-gray-500" />
        <span className="text-xs text-gray-400">No active execution</span>
        <Button
          variant="outline"
          size="sm"
          onClick={startExecution}
          disabled={!machineId || loading}
          className="h-6 px-2 text-xs ml-2"
        >
          <Play className="w-3 h-3 mr-1" />
          Start
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2 p-3 bg-black/60 backdrop-blur-sm rounded-lg border border-blue-500/30 shadow-lg">
      {/* Status Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Circle className="w-3 h-3 text-green-400 fill-green-400 animate-pulse" />
          </div>
          <span className="text-xs font-medium text-green-400">LIVE</span>
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-blue-400 border-blue-500/30">
            {snapshot?.active_state || 'Unknown'}
          </Badge>
          <span className="text-[10px] text-gray-500">{stepCount} steps</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={stopExecution}
          className="h-6 px-2 text-xs text-gray-400 hover:text-red-400"
        >
          Stop
        </Button>
      </div>

      {/* Quick Controls */}
      <div className="flex items-center space-x-2">
        {/* Event Selector */}
        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="h-7 w-[140px] text-xs">
            <SelectValue placeholder="Select event..." />
          </SelectTrigger>
          <SelectContent>
            {availableEvents.length > 0 ? (
              availableEvents.map((event) => (
                <SelectItem key={event.event_name} value={event.event_name}>
                  {event.event_name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="_none" disabled>No events</SelectItem>
            )}
          </SelectContent>
        </Select>

        {/* Dispatch Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                size="sm"
                onClick={dispatchEvent}
                disabled={loading || !selectedEvent}
                className="h-7 px-2"
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dispatch Event</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Step Back */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={stepBack}
                disabled={loading || stepCount === 0}
                className="h-7 px-2"
              >
                <SkipBack className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Step Back</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Reset */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={resetExecution}
                disabled={loading}
                className="h-7 px-2"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reset</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Deferred Effects Warning */}
      {snapshot?.deferred_effects?.length > 0 && (
        <div className="flex items-center text-[10px] text-yellow-400 bg-yellow-500/10 rounded px-2 py-1">
          <Zap className="w-3 h-3 mr-1" />
          {snapshot.deferred_effects.length} deferred effect(s)
        </div>
      )}
    </div>
  );
};

export default LiveExecutionOverlay;
